import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatBC } from "@/lib/format";
import { Toaster, toast } from "sonner";
import { ShoppingBag, Package, X, ImageIcon, Sparkles, Lock, Search, Tag, Truck, Ticket, TrendingUp, Share2, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import AdSlot from "@/components/AdSlot";
import { iconForCategory, ALL_CATEGORIES_ICON } from "@/lib/shopCategories";

export default function Shop() {
  const { user, refresh } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [activePromos, setActivePromos] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [activeSub, setActiveSub] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [buy, setBuy] = useState(null);
  const [shareFor, setShareFor] = useState(null); // product being shared
  const [myAffiliate, setMyAffiliate] = useState(null); // {code, commission_pct}
  const [form, setForm] = useState({ qty: 1, shipping_address: "", contact_number: "", points: 0, coins: 0 });
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(""); // the code we actually send to /loyalty/quote
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  // Debounce the search box so we don't hammer the server on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data.categories || []);
      setTotalProducts(data.total_products || 0);
    } catch (e) {
      // Category tree is non-fatal — the shop still renders with the "All" bucket.
      console.warn("Category tree load failed:", e);
    }
  };

  const loadPromotions = async () => {
    try {
      const { data } = await api.get("/promotions/active");
      setActivePromos(data.promotions || []);
    } catch (e) {
      console.warn("Active promos load failed:", e);
    }
  };

  const loadProducts = async () => {
    const params = {};
    if (activeCat !== "all") params.category = activeCat;
    if (activeSub) params.subcategory = activeSub;
    if (debouncedSearch) params.search = debouncedSearch;
    const { data } = await api.get("/products", { params });
    setProducts(data);
  };

  useEffect(() => { loadCategories(); loadPromotions(); loadMyAffiliate(); }, []);

  const loadMyAffiliate = async () => {
    try {
      const { data } = await api.get("/affiliate/my-code");
      setMyAffiliate(data);
    } catch (_e) { /* non-fatal */ }
  };
  useEffect(() => { loadProducts(); }, [activeCat, activeSub, debouncedSearch]);

  useEffect(() => {
    if (buy) {
      setForm({ qty: 1, shipping_address: "", contact_number: user?.mobile || "", points: 0, coins: 0 });
      setCouponInput("");
      setAppliedCoupon("");
    }
  }, [buy, user]);

  useEffect(() => {
    let cancelled = false;
    if (!buy) { setQuote(null); return; }
    const qty = Math.max(1, Math.min(buy.stock, Number(form.qty) || 1));
    const points = Math.max(0, Number(form.points) || 0);
    const coins = Math.max(0, Number(form.coins) || 0);
    (async () => {
      try {
        const params = { product_id: buy.id, qty, points, coins };
        if (appliedCoupon) params.coupon_code = appliedCoupon;
        const { data } = await api.get("/loyalty/quote", { params });
        if (!cancelled) setQuote(data);
      } catch (_e) { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
  }, [buy, form.qty, form.points, form.coins, appliedCoupon]);

  const applyCoupon = () => {
    const code = (couponInput || "").trim().toUpperCase();
    if (!code) return toast.error("Enter a coupon code first.");
    setAppliedCoupon(code);
  };
  const removeCoupon = () => {
    setAppliedCoupon("");
    setCouponInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Pull the affiliate ref that was saved from a public /p/:id?ref=<code>
      // landing. Only apply if it's still within its TTL window.
      let refCode = null;
      try {
        const saved = JSON.parse(localStorage.getItem("zfd_ref") || "null");
        if (saved && saved.code && saved.expires_at > Date.now()) refCode = saved.code;
      } catch { /* ignore malformed storage */ }
      const payload = {
        product_id: buy.id,
        qty: Number(form.qty),
        shipping_address: form.shipping_address,
        contact_number: form.contact_number,
        points_redeemed: Math.max(0, parseInt(form.points || 0, 10)),
        coins_redeemed: Math.max(0, parseInt(form.coins || 0, 10)),
      };
      if (appliedCoupon) payload.coupon_code = appliedCoupon;
      if (refCode) payload.ref_code = refCode;
      await api.post("/orders", payload);
      toast.success(`Order placed for ${buy.name}`);
      setBuy(null);
      await Promise.all([loadProducts(), loadCategories(), loadPromotions(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const activeCategoryObj = useMemo(
    () => categories.find((c) => c.slug === activeCat) || null,
    [activeCat, categories]
  );

  // Mobile pill row shows only buckets that have products (plus Uncategorized as
  // an escape hatch). Memoized so we don't re-filter on every render.
  const mobileCategoryPills = useMemo(
    () => categories.filter((c) => c.product_count > 0 || c.slug === "uncategorized"),
    [categories]
  );

  const clearFilters = () => { setActiveCat("all"); setActiveSub(""); setSearch(""); };
  const hasFilters = activeCat !== "all" || !!activeSub || !!debouncedSearch;

  return (
    <div className="space-y-7" data-testid="shop-root">
      <Toaster position="top-right" richColors />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">ZFD Storefront</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Spend BC. Get real goods.</h1>
          <p className="text-white/60 mt-1 text-sm max-w-xl">Curated drops, gift cards, and operator merch. Items ship to the address you provide.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="glass-card px-4 py-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Liquid BC</div>
            <div className="font-mono-num text-xl text-[#FBBF24]" data-testid="shop-balance">₱{formatBC(user?.active_balance)}</div>
          </div>
          <Link to="/shop/orders" className="text-xs px-3 py-2 rounded-md bg-white/5 border border-white/10 hover:border-[#FBBF24] hover:text-[#FBBF24] flex items-center gap-2" data-testid="goto-my-orders">
            <Package size={14} /> My Orders
          </Link>
        </div>
      </div>

      <AdSlot slot="shop-banner" variant="banner" />

      {/* Live promotional campaigns banner strip */}
      {activePromos.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="shop-promo-banner">
          {activePromos.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#FBBF24]/50 bg-gradient-to-r from-[#FBBF24]/[0.12] via-[#FBBF24]/[0.06] to-transparent text-xs"
              data-testid={`shop-promo-${p.id}`}
            >
              <Tag size={13} className="text-[#FBBF24]" />
              <span className="font-bold text-[#FBBF24]">{p.name}</span>
              <span className="text-white/60">·</span>
              <span className="font-mono-num text-white">-{Math.round(p.discount_pct * 100)}%</span>
              {p.scope === "category" && p.target_slug && (
                <span className="text-white/50">on <span className="text-white/75">{categories.find((c) => c.slug === p.target_slug)?.label || p.target_slug}</span></span>
              )}
              <span className="text-white/40 ml-auto hidden md:inline">
                ends {new Date(p.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="relative max-w-xl" data-testid="shop-search-wrap">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products by name or description…"
          className="zfd-input pl-9 pr-9"
          data-testid="shop-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/90 p-1"
            aria-label="Clear search"
            data-testid="shop-search-clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-6" data-testid="shop-layout">
        {/* ---------- Category rail (desktop = sidebar, mobile = horizontal pills) ---------- */}
        <aside className="md:sticky md:top-24 md:self-start" data-testid="shop-category-rail">
          {/* Mobile: horizontal scrolling pill row */}
          <div className="md:hidden -mx-1 overflow-x-auto pb-1">
            <div className="flex gap-2 px-1 min-w-max">
              <CategoryPill
                icon={ALL_CATEGORIES_ICON}
                label="All"
                count={totalProducts}
                active={activeCat === "all"}
                onClick={() => { setActiveCat("all"); setActiveSub(""); }}
                testid="cat-pill-all"
              />
              {mobileCategoryPills.map((c) => (
                <CategoryPill
                  key={c.slug}
                  icon={iconForCategory(c.icon)}
                  label={c.label}
                  count={c.product_count}
                  active={activeCat === c.slug}
                  onClick={() => { setActiveCat(c.slug); setActiveSub(""); }}
                  testid={`cat-pill-${c.slug}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: vertical rail */}
          <div className="hidden md:block glass-card p-3 space-y-0.5">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#FBBF24] font-bold px-2 py-2">Categories</div>
            <CategoryRow
              icon={ALL_CATEGORIES_ICON}
              label="All Categories"
              count={totalProducts}
              active={activeCat === "all"}
              onClick={() => { setActiveCat("all"); setActiveSub(""); }}
              testid="cat-row-all"
            />
            {categories.map((c) => (
              <CategoryRow
                key={c.slug}
                icon={iconForCategory(c.icon)}
                label={c.label}
                count={c.product_count}
                active={activeCat === c.slug}
                onClick={() => { setActiveCat(c.slug); setActiveSub(""); }}
                dimmed={c.product_count === 0 && c.slug !== "uncategorized"}
                testid={`cat-row-${c.slug}`}
              />
            ))}
          </div>
        </aside>

        {/* ---------- Product listing ---------- */}
        <div className="min-w-0 space-y-4">
          {/* Subcategory chip strip (only when a category with subs is selected) */}
          {activeCategoryObj && activeCategoryObj.subcategories.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="shop-subcategory-strip">
              <SubChip
                label={`All in ${activeCategoryObj.label}`}
                count={activeCategoryObj.product_count}
                active={!activeSub}
                onClick={() => setActiveSub("")}
                testid={`sub-chip-${activeCategoryObj.slug}-all`}
              />
              {activeCategoryObj.subcategories.map((s) => (
                <SubChip
                  key={s.name}
                  label={s.name}
                  count={s.product_count}
                  active={activeSub === s.name}
                  onClick={() => setActiveSub(activeSub === s.name ? "" : s.name)}
                  testid={`sub-chip-${activeCategoryObj.slug}-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          )}

          {/* Result summary bar */}
          {hasFilters && (
            <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-white/60" data-testid="shop-result-summary">
              <div>
                Showing <span className="text-white font-mono-num">{products.length}</span>{" "}
                {activeCat !== "all" && activeCategoryObj ? <>in <span className="text-[#FBBF24]">{activeCategoryObj.label}</span></> : "products"}
                {activeSub ? <> · <span className="text-[#FBBF24]">{activeSub}</span></> : null}
                {debouncedSearch ? <> · matching &ldquo;<span className="text-white">{debouncedSearch}</span>&rdquo;</> : null}
              </div>
              <button onClick={clearFilters} className="underline text-white/60 hover:text-white" data-testid="shop-clear-filters">
                Clear filters
              </button>
            </div>
          )}

          {products.length === 0 ? (
            <div className="glass-card p-12 text-center text-white/60" data-testid="shop-empty">
              <ShoppingBag className="mx-auto text-white/30 mb-4" size={36} />
              {hasFilters
                ? <>No products match these filters. <button onClick={clearFilters} className="underline text-[#FBBF24]" data-testid="shop-empty-clear">Clear filters</button></>
                : <>No products listed yet. Admin will stock the shelves soon.</>}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} activePromos={activePromos} onBuy={() => setBuy(p)} onShare={() => setShareFor(p)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {shareFor && myAffiliate && (
        <ShareToEarnModal
          product={shareFor}
          affiliate={myAffiliate}
          onClose={() => setShareFor(null)}
        />
      )}

      {buy && (() => {
        const qty = Number(form.qty) || 0;
        const subtotal = +(buy.price_bc * qty).toFixed(2);
        const shipping = +((buy.shipping_fee_bc || 0) * qty).toFixed(2);
        const total = +(subtotal + shipping).toFixed(2);
        const netCharge = quote?.net_charge_bc ?? total;
        const netInsufficient = netCharge > (user?.active_balance || 0);
        return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-5" data-testid="buy-modal">
          <form onSubmit={submit} className="glass-card gold-border-glow p-7 max-w-lg w-full">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Confirm Purchase</div>
                <h3 className="font-display text-2xl font-bold mt-1">{buy.name}</h3>
              </div>
              <button type="button" onClick={() => setBuy(null)} className="text-white/60 hover:text-white" data-testid="buy-close"><X /></button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="zfd-label">Qty (max {buy.stock})</label>
                <input type="number" min={1} max={buy.stock} className="zfd-input" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="buy-qty" />
              </div>
              <div>
                <label className="zfd-label">Contact #</label>
                <input required className="zfd-input" value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} data-testid="buy-contact" />
              </div>
            </div>
            <div className="mt-3">
              <label className="zfd-label">Shipping Address</label>
              <textarea required rows={3} className="zfd-input" value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} data-testid="buy-address" />
            </div>

            <div className="mt-4 bg-black/40 border border-[#FBBF24]/30 rounded-md p-3 space-y-1.5 text-xs">
              <div className="flex justify-between text-white/65"><span>Unit price</span><span className="font-mono-num">₱{formatBC(buy.price_bc)}</span></div>
              <div className="flex justify-between text-white/65"><span>Qty</span><span className="font-mono-num">× {form.qty || 0}</span></div>
              {quote?.promo_active ? (
                <>
                  <div className="flex justify-between text-white/50 line-through" data-testid="buy-original-subtotal">
                    <span>Original subtotal</span>
                    <span className="font-mono-num">₱{formatBC(quote.original_subtotal_bc)}</span>
                  </div>
                  <div className="flex justify-between text-[#FBBF24]" data-testid="buy-promo-row">
                    <span className="flex items-center gap-1"><Tag size={11} /> {quote.promo_name} ({Math.round(quote.promo_discount_pct * 100)}% off)</span>
                    <span className="font-mono-num">− ₱{formatBC(quote.promo_discount_bc)}</span>
                  </div>
                  <div className="flex justify-between text-white/65"><span>Subtotal after sale</span><span className="font-mono-num">₱{formatBC(quote.subtotal_bc)}</span></div>
                </>
              ) : (
                <div className="flex justify-between text-white/65"><span>Subtotal</span><span className="font-mono-num">₱{formatBC(quote?.subtotal_bc ?? (buy.price_bc * (Number(form.qty) || 0)))}</span></div>
              )}
              {(quote?.shipping_bc ?? (buy.shipping_fee_bc * (Number(form.qty) || 0))) > 0 && (
                <div className="flex justify-between text-white/65" data-testid="buy-shipping-row">
                  <span>Shipping ({form.qty || 0} × ₱{formatBC(quote?.shipping_per_item_bc ?? buy.shipping_fee_bc)}{quote?.shipping_tier_applied ? " · bulk tier" : ""})</span>
                  <span className="font-mono-num">+ ₱{formatBC(quote?.shipping_bc ?? (buy.shipping_fee_bc * (Number(form.qty) || 0)))}</span>
                </div>
              )}
              {quote?.shipping_tier_applied && quote?.shipping_per_item_bc === 0 && (
                <div className="flex justify-between text-emerald-300" data-testid="buy-free-shipping-row">
                  <span className="flex items-center gap-1"><Truck size={11} /> Bulk shipping unlocked</span>
                  <span className="font-mono-num">FREE</span>
                </div>
              )}
              {quote?.coupon_applied && (
                <div className="flex justify-between text-emerald-300" data-testid="buy-coupon-row">
                  <span className="flex items-center gap-1">
                    <Ticket size={11} /> Coupon <span className="font-mono-num font-bold">{quote.coupon_code}</span>
                    {quote.coupon_was_capped && <span className="text-white/50 text-[10px]">(capped at 30%)</span>}
                  </span>
                  <span className="font-mono-num">− ₱{formatBC(quote.coupon_discount_bc)}</span>
                </div>
              )}
              {quote && quote.points_applied > 0 && (
                <div className="flex justify-between text-emerald-300"><span>Loyalty discount ({quote.points_applied} pts)</span><span className="font-mono-num">− ₱{formatBC(quote.discount_bc)}</span></div>
              )}
              {quote && quote.coins_applied > 0 && (
                <div className="flex justify-between text-emerald-300" data-testid="buy-coins-row"><span className="flex items-center gap-1"><Coins size={11} /> ZenCoins ({quote.coins_applied} ZC)</span><span className="font-mono-num">− ₱{formatBC(quote.coin_discount_bc)}</span></div>
              )}
              <div className="flex justify-between pt-1.5 border-t border-white/10">
                <span className="text-[#FBBF24] uppercase tracking-wider text-[10px] font-bold">Net Charge</span>
                <span className="font-mono-num text-[#FBBF24] text-lg" data-testid="buy-total">₱{formatBC(quote?.net_charge_bc ?? total)}</span>
              </div>
              {netInsufficient && <div className="text-[#FCA5A5] text-[11px]">Insufficient Commission Credits — check your Commission Wallet balance.</div>}
            </div>

            {/* Coupon input */}
            <div className="mt-4 rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="buy-coupon-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#FBBF24] font-bold">
                  <Ticket size={12} /> Have a coupon code?
                </div>
                {quote?.coupon_applied && (
                  <button type="button" onClick={removeCoupon} className="text-[10px] text-[#FCA5A5] hover:text-white uppercase tracking-widest" data-testid="buy-coupon-remove">Remove</button>
                )}
              </div>
              {quote?.coupon_applied ? (
                <div className="text-xs text-emerald-300 flex items-center gap-2">
                  <CheckCouponIcon />
                  <span><strong className="font-mono-num">{quote.coupon_code}</strong> — {quote.coupon_name}</span>
                </div>
              ) : (
                <div className="flex items-stretch gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="WELCOME10"
                    className="zfd-input flex-1 font-mono-num tracking-widest"
                    data-testid="buy-coupon-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                  />
                  <button type="button" onClick={applyCoupon} className="px-3 py-2 rounded bg-[#FBBF24] text-[#0F2042] font-bold text-xs uppercase tracking-widest hover:bg-[#F0AF0A]" data-testid="buy-coupon-apply">Apply</button>
                </div>
              )}
              {quote?.coupon_error && (
                <div className="text-[#FCA5A5] text-[11px] mt-1.5" data-testid="buy-coupon-error">{quote.coupon_error}</div>
              )}
            </div>

            {quote && (
              quote.points_redeemable_on_product ? (
                <div className="mt-4 rounded-md border border-[#FBBF24]/30 bg-[#FBBF24]/[0.04] p-3" data-testid="buy-points-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#FBBF24] font-bold">
                      <Sparkles size={12} /> Loyalty Points
                    </div>
                    <div className="text-[10px] text-white/50 font-mono-num">
                      Wallet: <strong className="text-white/80">{quote.wallet_points}</strong> pts
                    </div>
                  </div>
                  {quote.max_points_allowed === 0 ? (
                    <div className="text-[11px] text-white/50">Add points to your wallet via daily claim to unlock discounts here.</div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max={Math.min(quote.max_points_allowed, quote.wallet_points)}
                          step="1"
                          value={form.points}
                          onChange={(e) => setForm({ ...form, points: parseInt(e.target.value, 10) })}
                          className="flex-1 accent-[#FBBF24]"
                          data-testid="buy-points-slider"
                        />
                        <input
                          type="number"
                          min="0"
                          max={Math.min(quote.max_points_allowed, quote.wallet_points)}
                          value={form.points}
                          onChange={(e) => setForm({ ...form, points: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                          className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-sm font-mono-num text-right"
                          data-testid="buy-points-input"
                        />
                      </div>
                      <div className="text-[10px] text-white/50 mt-1.5">
                        Cap: <span className="text-white/75 font-mono-num">{quote.max_points_allowed}</span> pts on this cart
                        (up to {(quote.max_discount_pct * 100).toFixed(0)}% off · {(1 / quote.points_php_ratio).toFixed(0)} pts = ₱1.00).
                        70% must be paid in BC.
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="mt-4 rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-2 text-[11px] text-white/55" data-testid="buy-points-blocked">
                  <Lock size={12} /> This item is low-margin — loyalty points cannot be redeemed against it.
                </div>
              )
            )}

            {/* Savings scoreboard — shipping-tier + coupon-min-cart nudges. */}
            <SavingsHint
              quote={quote}
              currentQty={Number(form.qty) || 0}
              maxQty={buy.stock}
              onBumpQty={(next) => setForm((f) => ({ ...f, qty: Math.min(buy.stock, next) }))}
              onBumpQtyByCoupon={(deltaBc) => {
                // Bump qty by enough units to clear the coupon min-cart floor.
                const unitAfterSale = quote?.promo_active
                  ? buy.price_bc * (1 - (quote.promo_discount_pct || 0))
                  : buy.price_bc;
                if (unitAfterSale <= 0) return;
                const extra = Math.ceil(deltaBc / unitAfterSale);
                setForm((f) => ({ ...f, qty: Math.min(buy.stock, (Number(f.qty) || 1) + extra) }));
              }}
            />

            {/* ZenCoin redemption panel — non-withdrawable coins earned from
                Marketing Center videos, capped by monthly + per-order rules. */}
            {quote && (
              <div className="mt-4 rounded-md border border-[#FBBF24]/30 bg-[#FBBF24]/[0.04] p-3" data-testid="buy-coins-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#FBBF24] font-bold">
                    <Coins size={12} /> ZenCoins Discount
                  </div>
                  <div className="text-[10px] text-white/50 font-mono-num">
                    Balance: <strong className="text-white/80">{quote.coins_available}</strong> ZC
                  </div>
                </div>
                {quote.coins_max_redeemable === 0 ? (
                  <div className="text-[11px] text-white/50">
                    {quote.coins_available === 0
                      ? <>Watch videos in <Link to="/marketing" className="text-[#FBBF24] hover:underline">Marketing Center</Link> to earn ZenCoins.</>
                      : `Monthly cap reached — ${quote.coins_monthly_remaining} coins left this month.`}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="0"
                        max={quote.coins_max_redeemable}
                        step="1"
                        value={form.coins}
                        onChange={(e) => setForm({ ...form, coins: parseInt(e.target.value, 10) })}
                        className="flex-1 accent-[#FBBF24]"
                        data-testid="buy-coins-slider"
                      />
                      <input
                        type="number"
                        min="0"
                        max={quote.coins_max_redeemable}
                        value={form.coins}
                        onChange={(e) => setForm({ ...form, coins: Math.min(quote.coins_max_redeemable, Math.max(0, parseInt(e.target.value || "0", 10))) })}
                        className="w-20 bg-black/40 border border-white/10 rounded px-2 py-1 text-sm font-mono-num text-right"
                        data-testid="buy-coins-input"
                      />
                    </div>
                    <div className="text-[10px] text-white/50 mt-1.5">
                      Cap: <span className="text-white/75 font-mono-num">{quote.coins_max_redeemable}</span> ZC on this cart
                      (up to {Number(quote.coin_max_pct_per_order).toFixed(0)}% off · 1 ZC = ₱{Number(quote.coin_php_value).toFixed(2)}).
                      Monthly remaining: <span className="font-mono-num text-white/75">{quote.coins_monthly_remaining}</span> · Per-order max: <span className="font-mono-num text-white/75">{quote.coins_max_per_order}</span>.
                    </div>
                  </>
                )}
              </div>
            )}
            <button disabled={busy || netInsufficient} type="submit" className="btn-deploy w-full mt-5 disabled:opacity-50" data-testid="buy-submit">
              {busy ? "Placing order…" : `Confirm Purchase · ₱${formatBC(quote?.net_charge_bc ?? total)}`}
            </button>
          </form>
        </div>
        );
      })()}
    </div>
  );
}

/** Vertical sidebar row on desktop */
function CategoryRow({ icon: Icon, label, count, active, onClick, dimmed, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-left transition-colors border ${
        active
          ? "bg-[#FBBF24]/15 border-[#FBBF24]/50 text-[#FBBF24]"
          : "border-transparent hover:bg-white/5 " + (dimmed ? "text-white/35" : "text-white/75")
      }`}
    >
      <Icon size={16} className="shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <span className="text-[10px] font-mono-num opacity-70">{count}</span>
    </button>
  );
}

/** Horizontal pill on mobile */
function CategoryPill({ icon: Icon, label, count, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
        active
          ? "bg-[#FBBF24] text-[#050B1A] border-[#FBBF24] font-bold"
          : "bg-white/5 text-white/75 border-white/10 hover:border-[#FBBF24]/40"
      }`}
    >
      <Icon size={12} />
      <span>{label}</span>
      <span className="opacity-70 font-mono-num">{count}</span>
    </button>
  );
}

/** Subcategory chip */
function SubChip({ label, count, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
        active
          ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
          : "bg-white/5 border-white/10 text-white/70 hover:border-[#93C5FD]/50 hover:text-white"
      }`}
    >
      {label} <span className="opacity-60 font-mono-num ml-1">{count}</span>
    </button>
  );
}

function ProductCard({ p, activePromos, onBuy, onShare }) {
  const imgSrc = p.has_image_data
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${p.image_url}`
    : p.image_url;

  // Compute the best-applicable discount for this card. Product-level promo is
  // reflected in p.promotional_sale.is_live; store-wide/category campaigns come
  // in via activePromos. UI just needs the max — the server is the source of
  // truth at checkout via /loyalty/quote.
  let bestPct = 0;
  let bestName = "";
  if (p.promotional_sale?.is_live) {
    bestPct = Number(p.promotional_sale.discount_pct) || 0;
    bestName = p.promotional_sale.name || "Sale";
  }
  for (const gp of activePromos || []) {
    if (gp.scope === "all" || (gp.scope === "category" && gp.target_slug === p.category)) {
      if (Number(gp.discount_pct) > bestPct) {
        bestPct = Number(gp.discount_pct);
        bestName = gp.name || "Sale";
      }
    }
  }
  const onSale = bestPct > 0;
  const salePrice = onSale ? +(p.price_bc * (1 - bestPct)).toFixed(2) : p.price_bc;

  return (
    <div className="glass-card overflow-hidden flex flex-col relative" data-testid={`product-card-${p.id}`}>
      {onSale && (
        <div
          className="absolute top-2 left-2 z-10 px-2 py-1 rounded-md bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-lg shadow-[#DC2626]/30"
          data-testid={`product-card-sale-badge-${p.id}`}
        >
          <Tag size={10} /> -{Math.round(bestPct * 100)}%
        </div>
      )}
      <div className="aspect-[4/3] bg-[#0a1526] flex items-center justify-center overflow-hidden">
        {imgSrc ? (
          <img src={imgSrc} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <ImageIcon className="text-white/20" size={32} />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold" data-testid={`product-card-category-${p.id}`}>
          {p.category_label}{p.subcategory ? <span className="text-white/45"> · {p.subcategory}</span> : null}
        </div>
        <h3 className="font-display text-lg font-bold mt-1">{p.name}</h3>
        <p className="text-xs text-white/55 mt-1 line-clamp-2 flex-1">{p.description}</p>
        <div className="mt-3 flex items-baseline justify-between">
          <div>
            {onSale ? (
              <>
                <div className="flex items-baseline gap-2">
                  <div className="font-mono-num text-2xl text-[#FBBF24]" data-testid={`product-card-sale-price-${p.id}`}>₱{formatBC(salePrice)}</div>
                  <div className="font-mono-num text-sm text-white/40 line-through" data-testid={`product-card-original-price-${p.id}`}>₱{formatBC(p.price_bc)}</div>
                </div>
                <div className="text-[10px] text-[#FCA5A5] mt-0.5 font-bold uppercase tracking-widest">{bestName}</div>
              </>
            ) : (
              <div className="font-mono-num text-2xl text-[#FBBF24]">₱{formatBC(p.price_bc)}</div>
            )}
            {p.shipping_fee_bc > 0 && (
              <div className="text-[10px] text-white/50 mt-0.5" data-testid={`product-card-ship-${p.id}`}>
                + ₱{formatBC(p.shipping_fee_bc)} shipping/item
              </div>
            )}
            {(p.shipping_tiers || []).length > 0 && (() => {
              // Show the biggest per-item saving unlock across product tiers.
              const base = Number(p.shipping_fee_bc) || 0;
              const best = (p.shipping_tiers || []).reduce((acc, t) => {
                const feeAt = t.free ? 0 : Number(t.fee_per_item_bc) || 0;
                const saving = (base - feeAt) * Number(t.min_qty || 0);
                return !acc || saving > acc.saving ? { min_qty: t.min_qty, free: !!t.free, saving } : acc;
              }, null);
              if (!best || best.saving <= 0) return null;
              return (
                <div className="text-[10px] text-emerald-300 mt-0.5 flex items-center gap-1" data-testid={`product-card-tiers-${p.id}`}>
                  <Truck size={10} />
                  {best.free ? "FREE" : `Cheaper`} shipping at qty {best.min_qty}+ · save ₱{formatBC(best.saving)}
                </div>
              );
            })()}
          </div>
          <div className="text-[10px] text-white/40 uppercase text-right">{p.stock > 0 ? `${p.stock} in stock` : "Sold out"}</div>
        </div>
        <button
          disabled={p.stock < 1}
          onClick={onBuy}
          className="btn-deploy mt-3 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          data-testid={`buy-btn-${p.id}`}
        >
          {p.stock < 1 ? "Sold out" : "Buy with BC"}
        </button>
        <button
          type="button"
          onClick={onShare}
          className="mt-2 text-xs text-white/70 hover:text-[#FBBF24] inline-flex items-center justify-center gap-1.5 py-1.5 rounded border border-white/10 hover:border-[#FBBF24]/40"
          data-testid={`share-btn-${p.id}`}
          title="Share this product publicly and earn 2% on every sale"
        >
          <Share2 size={11} /> Share to Earn 2%
        </button>
      </div>
    </div>
  );
}


// Small check icon shown next to an applied coupon (kept inline to avoid
// pulling in another lucide import at the module top).
function CheckCouponIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}


// -----------------------------------------------------------------------------
// ShareToEarnModal — inline share modal opened from any product card's
// "Share to Earn 2%" button. Consolidates copy-link + native web-share + QR.
// -----------------------------------------------------------------------------
function ShareToEarnModal({ product, affiliate, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const link = `${window.location.origin}/p/${product.id}?ref=${affiliate.code}`;
  const commissionPer = (product.price_bc * (affiliate.commission_pct || 0.02)).toFixed(2);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Clipboard blocked. Copy manually below."); }
  };

  const encoded = encodeURIComponent(link);
  const msg = encodeURIComponent(`Check out ${product.name} on Zenith Flow — get it here: ${link}`);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encoded}&color=ffffff&bgcolor=0F2042`;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-modal">
      <div className="glass-card gold-border p-6 max-w-lg w-full space-y-4 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white" data-testid="share-modal-close"><X size={18} /></button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold flex items-center gap-1.5">
            <Share2 size={12} /> Share &amp; Earn — 2% Commission
          </div>
          <h3 className="font-display text-xl font-bold mt-1 truncate">{product.name}</h3>
          <div className="text-xs text-white/60 mt-1">
            Every order placed via your link earns you <span className="text-[#FBBF24] font-bold">₱{commissionPer}</span> per single-item purchase. Commissions unlock on delivery.
          </div>
        </div>

        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
            className="zfd-input flex-1 font-mono-num text-xs"
            data-testid="share-modal-link"
          />
          <button
            onClick={doCopy}
            className="px-4 py-2 rounded bg-[#FBBF24] text-[#0F2042] font-bold text-xs uppercase tracking-widest hover:bg-yellow-300 inline-flex items-center gap-1.5"
            data-testid="share-modal-copy"
          >
            {copied ? <><CheckCouponIcon /> Copied</> : "Copy"}
          </button>
        </div>

        <div className="flex items-start gap-4">
          <img src={qrSrc} alt="QR" className="w-24 h-24 rounded border border-white/10 shrink-0" data-testid="share-modal-qr" />
          <div className="flex flex-wrap gap-1.5">
            <a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1" data-testid="share-modal-whatsapp"><Share2 size={11} /> WhatsApp</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encoded}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1" data-testid="share-modal-facebook"><Share2 size={11} /> Facebook</a>
            <a href={`https://twitter.com/intent/tweet?text=${msg}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1" data-testid="share-modal-twitter"><Share2 size={11} /> Twitter/X</a>
            <a href={`https://t.me/share/url?url=${encoded}&text=${encodeURIComponent(product.name)}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded bg-white/5 hover:bg-white/15 border border-white/10 inline-flex items-center gap-1" data-testid="share-modal-telegram"><Share2 size={11} /> Telegram</a>
          </div>
        </div>

        <div className="text-[10px] text-white/40 border-t border-white/10 pt-2">
          Your code: <span className="font-mono-num text-[#FBBF24] font-bold">{affiliate.code}</span> · Attribution: last-touch, 7-day window · <a href="/affiliate" className="underline hover:text-[#FBBF24]">See earnings dashboard →</a>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// SavingsHint — nudge pill shown above the "Place Order" button. Prioritises
// shipping-tier unlocks (usually the biggest saving) and falls back to coupon
// min-cart shortfalls. Silent when nothing is unlockable.
// -----------------------------------------------------------------------------
function SavingsHint({ quote, currentQty, maxQty, onBumpQty, onBumpQtyByCoupon }) {
  if (!quote) return null;

  // 1) Bulk-shipping tier nudge (highest signal — biggest savings).
  const hint = quote.next_shipping_hint;
  if (hint && hint.needed_qty_delta > 0 && hint.target_qty <= maxQty) {
    const label = hint.unlocks_free_shipping
      ? "unlock FREE shipping"
      : `unlock ₱${formatBC(hint.future_shipping_per_item_bc)}/item shipping`;
    return (
      <div
        className="mt-4 flex items-center gap-3 rounded-md border border-emerald-400/30 bg-emerald-400/[0.06] p-3"
        data-testid="buy-savings-hint"
      >
        <TrendingUp size={16} className="text-emerald-300 shrink-0" />
        <div className="text-xs flex-1">
          <div className="text-emerald-200 font-semibold" data-testid="buy-savings-hint-copy">
            Add <span className="font-mono-num font-bold">{hint.needed_qty_delta}</span> more{" "}
            {hint.needed_qty_delta === 1 ? "item" : "items"} to {label}.
          </div>
          <div className="text-white/55 mt-0.5">
            Estimated saving: <span className="text-emerald-300 font-mono-num font-bold">₱{formatBC(hint.estimated_savings_bc)}</span> at qty {hint.target_qty}.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onBumpQty(hint.target_qty)}
          className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded bg-emerald-400/20 hover:bg-emerald-400/30 text-emerald-200 whitespace-nowrap"
          data-testid="buy-savings-hint-apply"
        >
          Bump to {hint.target_qty}
        </button>
      </div>
    );
  }

  // Threshold just reached — small confirmation flash.
  if (quote.shipping_tier_applied && quote.shipping_per_item_bc === 0 && currentQty > 0) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-400/40 bg-emerald-400/[0.08] px-3 py-2 text-xs text-emerald-200" data-testid="buy-savings-unlocked">
        <CheckCouponIcon />
        <span>Free shipping unlocked at qty {currentQty}.</span>
      </div>
    );
  }

  // 2) Coupon min-cart shortfall nudge.
  const shortfall = quote.coupon_min_cart_shortfall_bc;
  if (shortfall && shortfall > 0 && quote.coupon_error && onBumpQtyByCoupon) {
    return (
      <div
        className="mt-4 flex items-center gap-3 rounded-md border border-[#FBBF24]/30 bg-[#FBBF24]/[0.05] p-3"
        data-testid="buy-savings-hint-coupon"
      >
        <Ticket size={16} className="text-[#FBBF24] shrink-0" />
        <div className="text-xs flex-1">
          <div className="text-[#FBBF24] font-semibold">
            Add <span className="font-mono-num font-bold">₱{formatBC(shortfall)}</span> more to activate coupon.
          </div>
          <div className="text-white/55 mt-0.5">
            One more click bumps qty to hit the coupon&apos;s min-cart floor.
          </div>
        </div>
        <button
          type="button"
          onClick={() => onBumpQtyByCoupon(shortfall)}
          className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded bg-[#FBBF24]/20 hover:bg-[#FBBF24]/30 text-[#FBBF24] whitespace-nowrap"
          data-testid="buy-savings-hint-coupon-apply"
        >
          Bump qty
        </button>
      </div>
    );
  }

  return null;
}
